import { HTMLAttributes } from 'react';
import tw from 'twin.macro';
import { Store } from 'react-notifications-component';
import { useTranslation } from 'react-i18next';

import { GarmDefinition } from '../episode_details_screen/GarmDefinition';
import { GARM_RISK_LEVEL } from '@insights_module/constants/garm';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { BookIcon } from '@icons/BookIcon';
import { Button } from '@components/Button';
import { useShowDetailsContext } from '@insights_module/context/ShowDetailsContext';
import { GarmRiskLevelType } from '@insights_module/types/BrandSafety';
import { useShowDetailsTableData } from '@insights_module/hooks/useShowDetailsTableData';

const Wrapper = tw.div`flex flex-col w-full bg-neutral-background`;
const RowWrapper = tw.div`cursor-pointer min-h-[33px] flex items-center justify-between text-[11px] font-medium text-text-and-icon p-1 rounded`;
const Flex = tw.div`flex items-center justify-start gap-2`;
const StyledSpan = tw.span`text-text-and-icon-muted text-[11px] leading-[13px] font-medium tracking-[.01em]`;
const StyledButton = tw(
  Button
)`text-text-and-icon-muted text-[11px] leading-[13px] font-medium underline tracking-[.01em]`;
const DictionaryButton = tw(Button)`p-0 w-auto h-auto`;

interface GarmOverviewItemProps extends HTMLAttributes<HTMLDivElement> {
  category: string;
  risk: GarmRiskLevelType;
  totalEpisodes: number;
  episodesInRisk: number;
  isLast?: boolean;
}

const GarmOverviewItem = (props: GarmOverviewItemProps) => {
  const { category, risk, totalEpisodes, episodesInRisk } = props;

  const { t } = useTranslation();
  const percentage = totalEpisodes ? Math.round((episodesInRisk / totalEpisodes) * 100) : 0;
  const { setSelectedShowDetailsGarmRisk, selectedShowDetailsGarmRisk } = useShowDetailsContext();

  const handleDictionaryClick = () => {
    Store.addNotification({
      insert: 'bottom',
      container: 'bottom-right',
      animationIn: ['animate__animated animate__fadeIn !rounded-md'],
      animationOut: ['animate__animated animate__fadeOut'],
      slidingEnter: {
        duration: 200,
        timingFunction: 'ease-out',
        delay: 0,
      },
      slidingExit: {
        duration: 200,
        timingFunction: 'ease-out',
        delay: 0,
      },
      dismiss: {
        duration: 0,
        showIcon: true,
      },
      width: 519,
      content: <GarmDefinition category={category} level={risk} />,
    });
  };

  const handleRiskSelect = () => {
    const value = selectedShowDetailsGarmRisk === risk ? null : risk;
    setSelectedShowDetailsGarmRisk(value);
  };

  return (
    <RowWrapper css={[selectedShowDetailsGarmRisk === risk && tw`bg-gray-2`]}>
      <Flex>
        <StyledButton
          tw='pl-1'
          onClick={handleRiskSelect}
          css={[selectedShowDetailsGarmRisk === risk && tw`text-accent-1`]}>
          {t(`insights.garm_overview.garm_risk_level.${risk}`, { percentage })}
        </StyledButton>
        <StyledSpan>{`(${String(episodesInRisk)}/${String(totalEpisodes)} ${t(
          'common.episodes'
        ).toLowerCase()})`}</StyledSpan>
      </Flex>
      <DictionaryButton onClick={handleDictionaryClick}>
        <BookIcon tw='stroke-text-and-icon-muted' />
      </DictionaryButton>
    </RowWrapper>
  );
};

const GarmOverviewDescription = () => {
  const { selectedGarmCategory } = useInsightsContext();
  const { garmOverview } = useShowDetailsTableData();
  const { selectedShowDetailsGarmCategory, episodeRelatedToGarmScoreCount } =
    useShowDetailsContext();

  return (
    <Wrapper>
      {Object.values(GARM_RISK_LEVEL).map((risk, index) => {
        const episodesInRisk = selectedShowDetailsGarmCategory
          ? garmOverview[selectedShowDetailsGarmCategory]?.[risk]
          : 0;

        return (
          <GarmOverviewItem
            category={selectedGarmCategory}
            key={risk}
            risk={risk}
            totalEpisodes={Number(episodeRelatedToGarmScoreCount)}
            episodesInRisk={episodesInRisk}
            isLast={index === Object.values(GARM_RISK_LEVEL).length - 1}
          />
        );
      })}
    </Wrapper>
  );
};

export { GarmOverviewDescription };
